import { Link } from 'react-router-dom'
import { MapPin, Users } from 'lucide-react'
import { RatingBadge } from './StarRating'
import { jobStatusLabel } from '../utils/jobStatus'
import { SubscriptionPlanPill } from './SubscriptionUI'
import { CategoryIcon } from '../utils/categoryIcons'

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (h < 1) return 'Az once'
  if (h < 24) return `${h} saat evvel`
  return `${d} gun evvel`
}

function CategoryIconBadge({ iconKey, size = 18, boxSize = 42, fontSize = '1.2rem' }) {
  return (
    <div
      style={{
        width: boxSize,
        height: boxSize,
        borderRadius: '.75rem',
        flexShrink: 0,
        background: 'rgba(var(--accent-rgb),.08)',
        border: '1px solid rgba(var(--accent-rgb),.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--accent)',
        fontSize,
      }}
    >
      <CategoryIcon iconKey={iconKey} size={size} />
    </div>
  )
}

export default function JobCard({ job, view = 'grid', index = 0 }) {
  const status = jobStatusLabel(job)

  if (view === 'list') {
    return (
      <Link to={`/jobs/${job.id}`} style={{ textDecoration: 'none', display: 'block', color: 'var(--text)' }}>
        <div
          className="card m-stagger-child card-interactive"
          style={{
            '--i': index,
            padding: '1.1rem 1.25rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '1rem',
            cursor: 'pointer',
          }}
        >
          <CategoryIconBadge iconKey={job.categoryIcon} boxSize={46} size={20} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-lg, 1.125rem)', marginBottom: '.2rem', color: 'var(--text)', lineHeight: 1.3 }}>
                  {job.title}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontSize: 'var(--text-sm, .875rem)', color: 'var(--muted)', flexWrap: 'wrap', marginTop: '.25rem' }}>
                  <span>{job.employerName}</span>
                  {job.employerSubscription?.vip && (
                    <SubscriptionPlanPill subscription={job.employerSubscription} compact roleLabel="VIP employer" />
                  )}
                  {(job.employerAvgRating || job.employerReviewCount > 0) && (
                    <RatingBadge avg={job.employerAvgRating} count={job.employerReviewCount} size=".8rem" />
                  )}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}><MapPin size={12} /> {job.location}</span>
                  <span>·</span>
                  <span>{timeAgo(job.createdAt)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.3rem', flexShrink: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '1.5rem', color: 'var(--text)', lineHeight: 1.1 }}>
                  {Number(job.budget).toLocaleString()} ₼
                </div>
                <span
                  style={{
                    fontSize: '.75rem',
                    fontWeight: 700,
                    padding: '.2rem .6rem',
                    borderRadius: '100px',
                    border: `1px solid ${status.border}`,
                    background: status.bg,
                    color: status.color,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {status.text}
                </span>
              </div>
            </div>

            <p
              style={{
                fontSize: 'var(--text-sm, .875rem)',
                color: 'var(--muted)',
                lineHeight: 1.55,
                marginTop: '.5rem',
                marginBottom: 0,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {job.description}
            </p>

            <div style={{ marginTop: '.45rem', fontSize: 'var(--text-xs, .75rem)', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}><Users size={12} /> {job.applicationCount} müraciət</span>
              {job.requiredExperienceYears > 0 && (
                <span style={{
                  fontWeight: 700, padding: '.1rem .5rem', borderRadius: '100px',
                  color: 'var(--muted)', background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)',
                  whiteSpace: 'nowrap',
                }}>
                  {job.requiredExperienceYears}+ il təcrübə
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <Link to={`/jobs/${job.id}`} style={{ textDecoration: 'none', color: 'var(--text)' }}>
      <div
        className="card m-stagger-child card-interactive"
        style={{
          '--i': index,
          padding: '1.25rem',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: '.85rem',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* ── Header: icon + title + status ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.85rem' }}>
          <CategoryIconBadge iconKey={job.categoryIcon} boxSize={50} size={22} fontSize="1.3rem" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 'var(--text-lg, 1.125rem)',
                marginBottom: '.25rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'var(--text)',
                lineHeight: 1.3,
              }}
            >
              {job.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontSize: 'var(--text-sm, .875rem)', color: 'var(--muted)', overflow: 'hidden' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {job.employerName}
              </span>
              {job.employerSubscription?.vip && (
                <SubscriptionPlanPill subscription={job.employerSubscription} compact roleLabel="VIP employer" />
              )}
              {(job.employerAvgRating || job.employerReviewCount > 0) && (
                <RatingBadge avg={job.employerAvgRating} count={job.employerReviewCount} size=".8rem" />
              )}
            </div>
          </div>
          <span
            style={{
              fontSize: '.75rem',
              fontWeight: 700,
              padding: '.25rem .65rem',
              borderRadius: '100px',
              border: `1px solid ${status.border}`,
              background: status.bg,
              color: status.color,
              flexShrink: 0,
            }}
          >
            {status.text}
          </span>
        </div>

        {/* ── Description ── */}
        <p
          style={{
            fontSize: 'var(--text-sm, .875rem)',
            color: 'var(--muted)',
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            margin: 0,
          }}
        >
          {job.description}
        </p>

        {/* ── Footer: meta + SALARY ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '.5rem',
            marginTop: 'auto',
            paddingTop: '.85rem',
            borderTop: '1px solid rgba(255,255,255,.06)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', minWidth: 0 }}>
            <span style={{ fontSize: 'var(--text-sm, .875rem)', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
              <MapPin size={13} /> {job.location}
            </span>
            <span style={{ fontSize: 'var(--text-xs, .75rem)', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
              <Users size={12} /> {job.applicationCount} müraciət · {timeAgo(job.createdAt)}
            </span>
            {job.requiredExperienceYears > 0 && (
              <span style={{
                fontSize: 'var(--text-xs, .75rem)', fontWeight: 700, alignSelf: 'flex-start',
                padding: '.1rem .5rem', borderRadius: '100px',
                color: 'var(--muted)', background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)',
                whiteSpace: 'nowrap',
              }}>
                {job.requiredExperienceYears}+ il təcrübə
              </span>
            )}
          </div>
          <div style={{ fontWeight: 800, fontSize: '1.5rem', color: 'var(--text)', lineHeight: 1.1, flexShrink: 0 }}>
            {Number(job.budget).toLocaleString()} ₼
          </div>
        </div>
      </div>
    </Link>
  )
}
